export interface Waypoint {
  id: string;
  locationKey: string;
  sequence: number;
  tasks: Task[];
  trip?: Trip;
  eta?: TripWaypointETA;
  tripId?: string;
}

export interface Proof {
  id: string;
  taskId: string;
  task: Task;
  mediaItems: ProofItem[];
  notes?: string;
}

export interface ProofItem {
  id: string;
  url: string;
  type: "PHOTO" | "VIDEO";
  key: string;
  createdAt: string;
  updatedAt: string;
}

export interface Task {
  id: string;
  taskNumber: number;
  address: {
    line1: string;
    line2: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
    location: {
      lat: number;
      lng: number;
    };
  };
  type:
    | "PICKUP"
    | "DELIVERY"
    | "DEPOT_RECEIVE"
    | "DEPOT_SORT"
    | "DEPOT_DISPATCH"
    | "RETURN";
  status: "PENDING" | "IN_PROGRESS" | "COMPLETED" | "FAILED" | "CANCELLED";
  description?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  parentTask?: Task;
  childTasks: Task[];
  proof?: Proof;
  order: Order;
  trip?: Trip;
  tripId?: string;
  operator?: Operator;
  failureReason?: string;
  failureReasonCode?: string;
}

export interface Operator {
  id: string;
  status: "ON_DUTY" | "OFF_DUTY" | "BLOCKED";
  user: User;
  lastLocation: {
    lat: number;
    lng: number;
    accuracy: number;
    altitude: number;
    heading: number;
    speed: number;
    timestamp: Date;
  };
  lastLocationTime?: string;
  isTracking?: boolean;
  depot: Depot;
  userId: string;
}
export enum OrderStatus {
  REGISTERED = "REGISTERED",
  PICKUP_PENDING = "PICKUP_PENDING",
  OUT_FOR_PICKUP = "OUT_FOR_PICKUP",
  PICKUP_ASSIGNED = "PICKUP_ASSIGNED",
  PICKED_UP = "PICKED_UP",
  DEPOT_RECEIVE_PENDING = "DEPOT_RECEIVE_PENDING",
  DEPOT_RECEIVE_IN_PROGRESS = "DEPOT_RECEIVE_IN_PROGRESS",
  AT_DEPOT = "AT_DEPOT",
  DEPOT_SORT_PENDING = "DEPOT_SORT_PENDING",
  DEPOT_SORT_IN_PROGRESS = "DEPOT_SORT_IN_PROGRESS",
  SORTED = "SORTED",
  DEPOT_DISPATCH_PENDING = "DEPOT_DISPATCH_PENDING",
  DEPOT_DISPATCH_IN_PROGRESS = "DEPOT_DISPATCH_IN_PROGRESS",
  IN_TRANSIT = "IN_TRANSIT",
  DELIVERY_PENDING = "DELIVERY_PENDING",
  OUT_FOR_DELIVERY = "OUT_FOR_DELIVERY",
  DELIVERED = "DELIVERED",
  RETURN_PENDING = "RETURN_PENDING",
  RETURN_IN_PROGRESS = "RETURN_IN_PROGRESS",
  RETURNED = "RETURNED",
  PICKUP_FAILED = "PICKUP_FAILED",
  DEPOT_RECEIVE_FAILED = "DEPOT_RECEIVE_FAILED",
  DEPOT_SORT_FAILED = "DEPOT_SORT_FAILED",
  DEPOT_DISPATCH_FAILED = "DEPOT_DISPATCH_FAILED",
  DELIVERY_FAILED = "DELIVERY_FAILED",
  RETURN_FAILED = "RETURN_FAILED",
  PICKUP_CANCELLED = "PICKUP_CANCELLED",
  DEPOT_RECEIVE_CANCELLED = "DEPOT_RECEIVE_CANCELLED",
  DEPOT_SORT_CANCELLED = "DEPOT_SORT_CANCELLED",
  DEPOT_DISPATCH_CANCELLED = "DEPOT_DISPATCH_CANCELLED",
  DELIVERY_CANCELLED = "DELIVERY_CANCELLED",
  RETURN_CANCELLED = "RETURN_CANCELLED",
}
export interface Parcel {
  id: string;
  trackingNumber: string;
  status: string;
  weight: number;
  dimensions?: {
    width: number;
    height: number;
    length: number;
  };
  description: string;
  isFragile: boolean;
  requiresCooling: boolean;
}
export interface Order {
  id: string;
  orderNumber: string;
  type: "STANDARD" | "EXPRESS" | "SAME_DAY";
  pickupAddress: Address;
  deliveryAddress: Address;
  senderName: string;
  senderPhone: string;
  senderEmail: string;
  pickupNotes: string;
  recipientName: string;
  recipientPhone: string;
  recipientEmail: string;
  deliveryNotes: string;
  parcels: Parcel[];
  totalFees?: number;
  appliedFees?: { type: string; amount: number }[];
  status: string;
  createdAt: string;
  updatedAt: string;
  manualPricingAdjustment: number;
  tasks?: Task[];
  placerCustomer?: Customer;
  orderTags?: Tag[];
}

export enum DepotType {
  HUB = "HUB",
  WAREHOUSE = "WAREHOUSE",
  MICRO_HUB = "MICRO_HUB",
}

export interface Address {
  line1: string;
  line2: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  location: {
    lat: number;
    lng: number;
  };
}

export interface Depot {
  id: string;
  name: string;
  depotNumber: string;
  type: DepotType;
  status: "ACTIVE" | "INACTIVE" | "DELETED";
  currentCapacity: {
    small: number;
    medium: number;
    large: number;
  };
  capacity: {
    small: number;
    medium: number;
    large: number;
  };
  operatingHours: {
    day: number;
    open: string;
    close: string;
  }[];
  address: Address;
  serviceRadius: number;
}

export interface Trip {
  id: string;
  startTime?: string;
  endTime?: string;
  distance?: number;
  estimatedDuration?: number;
  actualDuration?: number;
  operatorId: string;
  tripNumber: number;
  color?: string;
  status: "PLANNED" | "ACTIVE" | "COMPLETED" | "ARCHIVED";
  operator?: Operator;
  vehicle?: Vehicle;
  plannedRoute?: {
    routes: {
      polyline: {
        encodedPolyline: string;
      };
    }[];
  };
  waypoints: Waypoint[];
  tasks: Task[];
  depot: Depot;
  createdAt: string;
  updatedAt?: string;
  completedTasks?: number;
  failedTasks?: number;
  totalTasks?: number;
  useCustomStartLocation?: boolean;
  useCustomEndLocation?: boolean;
  startLocation?: Record<
    string,
    {
      latitude: number;
      longitude: number;
      address: string;
    } | null
  >;
  endLocation?: Record<
    string,
    {
      latitude: number;
      longitude: number;
      address: string;
    }
  >;
}

export interface Vehicle {
  id: string;
  plateNumber: string;
}

export interface ServiceArea {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
  boundary: {
    type: "Polygon";
    coordinates: number[][][];
  };
  postcodes: string[];
  maxDailyOrders?: number;
  maxVehicles?: number;
  operatingWindows: {
    id: string;
    type: "PICKUP" | "DELIVERY";
    daysOfWeek: number[];
    startTime: string;
    endTime: string;
    capacity: number;
    isActive: boolean;
  }[];
  depot: Depot;
  depotId: string;
  metadata: Record<string, any>;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  picture?: string;
}

export interface Customer {
  id: string;
  customerNumber: number;
  type: "INDIVIDUAL" | "BUSINESS";
  status: "ACTIVE" | "INACTIVE" | "DELETED";
  user: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    picture?: string;
  };
  businessAccount?: {
    companyName: string;
    taxNumber?: string;
  };
  tier?: CustomerTier;
  tierId?: string;
  billing?: {
    id: string;
    paymentStyle:
      | "MANUAL"
      | "PAY_PER_ORDER"
      | "MONTHLY_FIRST"
      | "WEEKLY_SUNDAY";
    stripeCustomerId?: string;
  };
  userId: string;
}
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    totalCount: number;
    currentPage: number;
    totalPages: number;
  };
}
export interface CustomerTier {
  id: string;
  name: string;
  type: "CUSTOM" | "DEFAULT";
  description: string;
  discountPercentage: number;
  freePickups: number;
  prioritySupport: boolean;
  minimumSpend: number;
  minimumShipments: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Invoice {
  id: string;
  customer: {
    id: string;
    user: {
      firstName: string;
      lastName: string;
      email: string;
    };
  };
  stripeInvoiceData: {
    id: string;
    status: string;
    number: string;
    hostedUrl: string | null;
    pdfUrl: string | null;
    amount: number;
    dueDate: string | null;
  };
  totalOrders: number;
  orderDateRange: {
    start: string;
    end: string;
  } | null;
  createdAt: string;
}
interface PricingOption {
  speedType: string;
  estimatedDeliveryTime: { minHours: number; maxHours: number };
  price: number;
}
export interface ShippingOptionsResponse {
  shippingOptions: PricingOption[];
  reason?: string;
  pickupServiceArea: {
    id: string;
    name: string;
    depotId: string;
  } | null;
  deliveryServiceArea: {
    id: string;
    name: string;
    depotId: string;
  } | null;
  totalWeight: number;
  totalDimensionalWeight: number;
  chargeableWeight: number;
}

export type FeeType =
  | "domestic-base"
  | "dimensional-weight"
  | "speed"
  | "special-handling"
  | "pickup-handling"
  | "international-base"
  | "international-surcharge";

export enum FeeScope {
  DOMESTIC = "DOMESTIC",
  INTERNATIONAL = "INTERNATIONAL",
  ANY = "ANY",
}

export enum DeliverySpeed {
  SAME_DAY = "SAME_DAY",
  EXPRESS = "EXPRESS",
  STANDARD = "STANDARD",
  SCHEDULED = "SCHEDULED",
}

export enum HandlingType {
  FRAGILE = "FRAGILE",
  COOLING = "COOLING",
  DANGEROUS = "DANGEROUS",
}

export interface Fee {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  // Common fields above, specific fields below based on fee type
  fee?: number;
  feeScope?: "DOMESTIC" | "INTERNATIONAL" | "ANY";
  type?: string;
  basePrice?: number;
  originCountry?: string;
  destinationCountry?: string;
  pickupServiceAreaId?: string;
  deliveryServiceAreaId?: string;
  minWeight?: number;
  maxWeight?: number;
  pricePerKg?: number;
}

export interface DomesticBaseFee {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  pickupServiceArea: ServiceArea;
  deliveryServiceArea: ServiceArea;
  basePrice: number;
  customerTier?: CustomerTier;
}

export interface SpeedFee {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  type: DeliverySpeed;
  fee: number;
  feeScope: FeeScope;
  description?: string;
  minDeliveryTime?: number;
  maxDeliveryTime?: number;
  timeUnit?: "HOURS" | "DAYS";
  customerTier?: CustomerTier;
  pickupServiceArea?: ServiceArea;
  deliveryServiceArea?: ServiceArea;
}

export interface SpecialHandlingFee {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  type: HandlingType;
  fee: number;
  feeScope: FeeScope;
  customerTier?: CustomerTier;
}

export interface DimensionalWeightFee {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  minWeight: number;
  maxWeight: number;
  pricePerKg: number;
  fee: number;
  baseDimWeight: number;
  maxSideLength: number;
  factor: number;
  feeScope: FeeScope;
  customerTier?: CustomerTier;
}

export interface AddressMedia {
  id: string;
  name?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  addressId?: string;
  // Fields directly from the database
  placeId?: string;
  streetNumber?: string;
  streetName?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  // Optional nested address object for backward compatibility
  address?: Address;
  mediaItems: AddressMediaItem[];
}

export interface AddressMediaItem {
  id: string;
  url: string;
  type: "PHOTO" | "VIDEO";
  key: string;
}

export interface Tag {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string; // Unicode emoji (e.g. "🚚") or icon reference with prefix (e.g. "icon:shopify")
  type: "SYSTEM" | "CUSTOMER" | "SHARED";
  createdBy: string;
  isActive: boolean;
  categoryId?: string;
  category?: TagCategory;
  createdAt: string;
  updatedAt: string;
}

export interface TagCategory {
  id: string;
  name: string;
  description?: string;
  createdBy: string;
  isActive: boolean;
  tags?: Tag[];
  createdAt: string;
  updatedAt: string;
}

export interface TripWaypointETA {
  id: string;
  waypointId: string;
  estimatedArrivalTime: string;
  estimatedDepartureTime: string;
  estimatedDuration: number; // in minutes
  actualArrivalTime?: string;
  actualDepartureTime?: string;
  lastUpdated: string;
}
